const phpFormatter = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  currencyDisplay: "narrowSymbol",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactNumberFormatter = new Intl.NumberFormat("en-PH", {
  maximumFractionDigits: 1,
});

// Marketplace cards switch to abbreviated prices at ₱10,000 to keep every
// Explore tab equally scannable without losing useful precision.
const COMPACT_PRICE_THRESHOLD = 10_000;
const ONE_THOUSAND = 1_000;
const ONE_MILLION = 1_000_000;

export function formatPhp(value: number | string | null | undefined): string {
  const amount = Number(value);
  return phpFormatter.format(Number.isFinite(amount) ? amount : 0);
}

/** Quotation/amendment forms use safe integer centavos for exact stage totals.
 * Invalid drafts return null so callers can show validation without rounding them.
 * Precision authorization remains with each source-specific form and the server.
 */
export function pesoCentavos(value: number): number | null {
  const scaled = value * 100;
  const rounded = Math.round(scaled);
  return Number.isFinite(value) && Number.isSafeInteger(rounded) && Math.abs(scaled - rounded) <= 0.00001 ? rounded : null;
}

export function sumPesos(values: number[]): number | null {
  let total = 0;
  for (const value of values) {
    const centavos = pesoCentavos(value);
    if (centavos === null) return null;
    total += centavos;
  }
  return Number.isSafeInteger(total) ? total / 100 : null;
}

/** Keeps marketplace price labels compact while financial records use formatPhp. */
export function formatCompactPhp(value: number | string | null | undefined): string {
  return formatCompactPhpAtThreshold(value, COMPACT_PRICE_THRESHOLD);
}

/** Shortens card prices starting at ₱1,000 while regular layouts keep full values. */
export function formatCompactCardPhp(value: number | string | null | undefined): string {
  return formatCompactPhpAtThreshold(value, ONE_THOUSAND);
}

function formatCompactPhpAtThreshold(value: number | string | null | undefined, threshold: number): string {
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const absoluteAmount = Math.abs(safeAmount);

  if (absoluteAmount < threshold) return formatPhp(safeAmount);

  const isMillion = absoluteAmount >= ONE_MILLION;
  const divisor = isMillion ? ONE_MILLION : ONE_THOUSAND;
  const suffix = isMillion ? "M" : "k";
  const compactAmount = Number((safeAmount / divisor).toFixed(1));

  if (!isMillion && Math.abs(compactAmount) >= ONE_THOUSAND) {
    return `₱${compactNumberFormatter.format(safeAmount / ONE_MILLION)}M`;
  }

  return `₱${compactNumberFormatter.format(compactAmount)}${suffix}`;
}

/**
 * Abbreviates peso values embedded in worker rates and job salary ranges while
 * preserving surrounding text such as a billing period or range separator.
 */
export function formatCompactPhpText(value: string | null | undefined): string {
  return formatCompactPhpTextAtThreshold(value, COMPACT_PRICE_THRESHOLD);
}

export function formatCompactCardPhpText(value: string | null | undefined): string {
  return formatCompactPhpTextAtThreshold(value, ONE_THOUSAND);
}

function formatCompactPhpTextAtThreshold(value: string | null | undefined, threshold: number): string {
  const price = (numericValue: string) => {
    const amount = Number(numericValue.replace(/,/g, ""));
    if (!Number.isFinite(amount)) return `₱${numericValue}`;
    if (Math.abs(amount) >= threshold) return formatCompactPhpAtThreshold(amount, threshold);
    const [whole, fraction] = numericValue.replace(/,/g, "").split(".");
    return `₱${Number(whole).toLocaleString("en-PH")}${fraction === undefined ? "" : `.${fraction}`}`;
  };
  // Currency-marked ranges share a unit; format both endpoints without interpreting unrelated numbers.
  return String(value ?? "").replace(/(?:₱|\bPHP\s*)([\d,]+(?:\.\d+)?)(?:\s*([-–—])\s*(?:₱|PHP\s*)?([\d,]+(?:\.\d+)?))?/gi,
    (_match, first: string, separator: string | undefined, last: string | undefined) =>
      last ? `${price(first)}${separator}${price(last)}` : price(first));
}
